import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useIsLargeOrAbove } from "@jugl-web/utils/hooks/useMediaQuery";
import { SidebarNavContext } from "./SidebarNavContext";

/**
 * Generic overlay state provider for any sidebar navigation (including CX's and ORG's home sidebars).
 * Closes automatically at the `lg` breakpoint, on Escape, and on route change.
 */
export const SidebarNavProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const isLargeOrAbove = useIsLargeOrAbove();
  const { pathname } = useLocation();

  const openOverlay = useCallback(() => {
    setIsOverlayOpen(true);
  }, []);

  const closeOverlay = useCallback(() => {
    setIsOverlayOpen(false);
  }, []);

  const toggleOverlay = useCallback(() => {
    setIsOverlayOpen((isOpen) => !isOpen);
  }, []);

  useEffect(() => {
    if (isLargeOrAbove) {
      setIsOverlayOpen(false);
    }
  }, [isLargeOrAbove]);

  useEffect(() => {
    setIsOverlayOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOverlayOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOverlayOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOverlayOpen]);

  const value = useMemo(
    () => ({
      isOverlayOpen,
      openOverlay,
      closeOverlay,
      toggleOverlay,
    }),
    [isOverlayOpen, openOverlay, closeOverlay, toggleOverlay]
  );

  return (
    <SidebarNavContext.Provider value={value}>
      {children}
    </SidebarNavContext.Provider>
  );
};
